import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Building2, BookOpen, Calendar, Users, Settings, 
  Shield, Eye, EyeOff, Mail, Phone, Globe, Clock, Plus,
  ChevronRight, Landmark, Heart, Edit2, UserPlus, MoreVertical
} from 'lucide-react';
import { InfoTooltip } from '../../components/ExplanationLayers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * INSTITUTION PROFILE (BUILD 3)
 * 
 * Institution profiles are NOT social profiles.
 * 
 * Profile includes:
 * - Name
 * - Logo / banner
 * - Description
 * - Institution type badge
 * - Public / Private toggle
 * - Contact methods
 * - Steward list (Primary + Assistant REQUIRED)
 * - Linked Commons
 * - Linked Calendars
 * - Linked Student Notebooks (if school)
 * 
 * NO feed, NO likes, NO comments
 */

const TYPE_CONFIG = {
  school: { icon: Building2, color: 'var(--lk-earth-brown)', label: 'School' },
  program: { icon: Calendar, color: 'var(--lk-secondary-orange)', label: 'Educational Program' },
  nonprofit: { icon: Heart, color: 'var(--lk-primary-green)', label: 'Nonprofit' },
  institution: { icon: Landmark, color: 'var(--lk-soft-stone)', label: 'Institution' }
};

export default function InstitutionProfile() {
  const navigate = useNavigate();
  const { institutionId } = useParams();
  const { user } = useContext(AuthContext);
  
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadInstitution();
  }, [institutionId]);

  const loadInstitution = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/api/institutions/${institutionId}`, { headers });
      setInstitution(response.data);
      setLoadError(null);
    } catch (error) {
      console.error('Failed to load institution:', error);
      // Calm error whisper — no auto-navigate, user stays on page (matches
      // the pattern used on ActivityDetail / GroupProfile).
      setLoadError(error?.response?.status === 404 ? 'not_found' : 'transient');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-earth-brown)' }} />
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
          <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:opacity-70" aria-label="Back">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-8">
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }} data-testid="institution-load-error">
            {loadError === 'not_found' ? 'Institution not found.' : "Couldn't load right now. Try again in a moment."}
          </p>
        </main>
      </div>
    );
  }

  const typeConfig = TYPE_CONFIG[institution.type] || TYPE_CONFIG.institution;
  const TypeIcon = typeConfig.icon;
  const isSteward = institution.stewards?.some(s => s.user_id === user?.user_id);
  const isPrimarySteward = institution.primary_steward_id === user?.user_id;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 text-center">
              <h1 className="font-semibold truncate" style={{ color: 'var(--fy-text-primary)' }}>{institution.name}</h1>
            </div>
            {isSteward && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => navigate(`/institutions/${institutionId}/dashboard`)}
                  className="px-3 py-1.5 text-xs font-medium rounded-full"
                  style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
                  data-testid="institution-dashboard-btn"
                >
                  Dashboard
                </button>
                <button onClick={() => navigate(`/institutions/${institutionId}/settings`)} className="p-2 -mr-2">
                  <Settings className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto pb-20">
        {/* Banner & Logo */}
        <div className="relative">
          <div 
            className="h-32"
            style={{ 
              backgroundColor: typeConfig.color,
              backgroundImage: institution.banner_url ? `url(${institution.banner_url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute -bottom-10 left-4">
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden border-4"
              style={{ 
                backgroundColor: 'var(--fy-surface-card)', 
                borderColor: 'var(--fy-background-primary)'
              }}
            >
              {institution.logo_url ? (
                <img src={institution.logo_url} alt={institution.name} className="w-full h-full object-cover" />
              ) : (
                <TypeIcon className="w-10 h-10" style={{ color: typeConfig.color }} />
              )}
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="px-4 pt-14 pb-4">
          {/* Type Badge */}
          <div className="flex items-center gap-2 mb-2">
            <span 
              className="text-xs px-2 py-1 rounded-full font-medium"
              style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}
            >
              {typeConfig.label}
            </span>
            <span 
              className="text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1"
              style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}
            >
              {institution.visibility === 'public' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {institution.visibility === 'public' ? 'Public' : 'Private'}
            </span>
          </div>

          {/* Name */}
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
            {institution.name}
          </h1>

          {/* Description */}
          {institution.description && (
            <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              {institution.description}
            </p>
          )}

          {/* Contact Methods */}
          {(institution.contact_email || institution.contact_phone) && (
            <div className="flex flex-wrap gap-3 mb-4">
              {institution.contact_email && (
                <a 
                  href={`mailto:${institution.contact_email}`}
                  className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full"
                  style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)' }}
                >
                  <Mail className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
                  {institution.contact_email}
                </a>
              )}
              {institution.contact_phone && (
                <a 
                  href={`tel:${institution.contact_phone}`}
                  className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full"
                  style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)' }}
                >
                  <Phone className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
                  {institution.contact_phone}
                </a>
              )}
            </div>
          )}

          {/* Steward Actions */}
          {isSteward && (
            <div className="flex gap-2 mb-4">
              <Button 
                onClick={() => navigate(`/institutions/${institutionId}/edit`)}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="edit-institution-btn"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button 
                onClick={() => navigate(`/institutions/${institutionId}/invite`)}
                size="sm"
                className="rounded-full"
                style={{ backgroundColor: typeConfig.color, color: 'var(--lk-warm-sand)' }}
                data-testid="invite-member-btn"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Invite
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="px-4 border-b" style={{ borderColor: 'var(--fy-border-subtle)' }}>
          <div className="flex gap-1">
            {['overview', 'calendar', 'members', institution.type === 'school' ? 'classes' : null].filter(Boolean).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="px-4 py-3 text-sm font-medium capitalize relative"
                style={{ 
                  color: activeTab === tab ? typeConfig.color : 'var(--fy-text-secondary)'
                }}
              >
                {tab}
                {activeTab === tab && (
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ backgroundColor: typeConfig.color }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-4 py-4">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {/* Stewards */}
              <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
                    <Shield className="w-4 h-4" style={{ color: typeConfig.color }} />
                    Stewards
                    <InfoTooltip topic="dual_steward" />
                  </h3>
                  {isPrimarySteward && (
                    <button className="text-xs" style={{ color: typeConfig.color }}>Manage</button>
                  )}
                </div>
                <div className="space-y-2">
                  {institution.stewards?.length > 0 ? institution.stewards.map((steward, idx) => (
                    <div key={steward.user_id || idx} className="flex items-center gap-3 p-2 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                      <img 
                        src={steward.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(steward.name || 'S')}&background=8B6A4F&color=fff&size=32`}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>{steward.name || 'Steward'}</p>
                        <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                          {steward.role === 'primary' ? 'Primary Steward' : 'Assistant Steward'}
                        </p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>No stewards assigned</p>
                  )}
                  {institution.stewards?.length < 2 && isSteward && (
                    <button 
                      onClick={() => navigate(`/institutions/${institutionId}/invite?role=steward`)}
                      className="w-full py-2 rounded-lg text-sm font-medium border-2 border-dashed flex items-center justify-center gap-2"
                      style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-secondary)' }}
                    >
                      <Plus className="w-4 h-4" />
                      Add Assistant Steward (Required)
                    </button>
                  )}
                </div>
              </Card>

              {/* Linked Commons */}
              {institution.commons?.length > 0 && (
                <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
                  <h3 className="font-semibold flex items-center gap-2 mb-3" style={{ color: 'var(--fy-text-primary)' }}>
                    <Landmark className="w-4 h-4" style={{ color: 'var(--lk-earth-brown)' }} />
                    Linked Commons
                  </h3>
                  <div className="space-y-2">
                    {institution.commons.map((commons, idx) => (
                      <button 
                        key={commons.commons_id || idx}
                        onClick={() => navigate(`/commons/${commons.commons_id}`)}
                        className="w-full p-3 rounded-lg flex items-center gap-3 text-left hover:bg-black/5"
                        style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                      >
                        <Landmark className="w-5 h-5" style={{ color: 'var(--lk-earth-brown)' }} />
                        <span className="flex-1 font-medium" style={{ color: 'var(--fy-text-primary)' }}>{commons.name}</span>
                        <ChevronRight className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-3 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                  <p className="text-lg font-bold" style={{ color: typeConfig.color }}>{institution.member_count || 0}</p>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Members</p>
                </Card>
                <Card className="p-3 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                  <p className="text-lg font-bold" style={{ color: typeConfig.color }}>{institution.event_count || 0}</p>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Events</p>
                </Card>
                {institution.type === 'school' && (
                  <Card className="p-3 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                    <p className="text-lg font-bold" style={{ color: typeConfig.color }}>{institution.class_count || 0}</p>
                    <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Classes</p>
                  </Card>
                )}
              </div>

              {/* No Feed Notice */}
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                <Shield className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--fy-text-disabled)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--fy-text-secondary)' }}>
                  This is not a social profile
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
                  No feed, no likes, no comments. Just coordination.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'calendar' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Upcoming Events</h3>
                {isSteward && (
                  <Button 
                    size="sm" 
                    className="rounded-full"
                    style={{ backgroundColor: typeConfig.color, color: 'var(--lk-warm-sand)' }}
                    onClick={() => navigate(`/institutions/${institutionId}/calendar/create`)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Event
                  </Button>
                )}
              </div>
              
              {institution.upcoming_events?.length > 0 ? (
                <div className="space-y-2">
                  {institution.upcoming_events.map((event, idx) => (
                    <Card 
                      key={event.event_id || idx}
                      className="p-3 flex items-center gap-3 cursor-pointer hover:shadow-sm"
                      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                      onClick={() => navigate(`/calendar/event/${event.event_id}`)}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${typeConfig.color}20` }}
                      >
                        <Calendar className="w-5 h-5" style={{ color: typeConfig.color }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>{event.title}</p>
                        <p className="text-xs flex items-center gap-1" style={{ color: 'var(--fy-text-secondary)' }}>
                          <Clock className="w-3 h-3" />
                          {new Date(event.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
                  <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>No upcoming events</p>
                  {isSteward && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 rounded-full"
                      onClick={() => navigate(`/institutions/${institutionId}/calendar/create`)}
                    >
                      Create First Event
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'members' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Members</h3>
                {isSteward && (
                  <Button 
                    size="sm" 
                    className="rounded-full"
                    style={{ backgroundColor: typeConfig.color, color: 'var(--lk-warm-sand)' }}
                    onClick={() => navigate(`/institutions/${institutionId}/invite`)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Invite
                  </Button>
                )}
              </div>
              
              {institution.members?.length > 0 ? (
                <div className="space-y-2">
                  {institution.members.map((member, idx) => (
                    <div 
                      key={member.user_id || idx}
                      className="p-3 rounded-lg flex items-center gap-3"
                      style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                    >
                      <img 
                        src={member.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || 'M')}&background=7A8F5B&color=fff&size=40`}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>{member.name}</p>
                        <p className="text-xs capitalize" style={{ color: 'var(--fy-text-secondary)' }}>{member.role || 'Member'}</p>
                      </div>
                      {isSteward && (
                        <button className="p-1">
                          <MoreVertical className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
                  <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>No members yet</p>
                  {isSteward && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 rounded-full"
                      onClick={() => navigate(`/institutions/${institutionId}/invite`)}
                    >
                      Invite Members
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'classes' && institution.type === 'school' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Classes</h3>
                {isSteward && (
                  <Button 
                    size="sm" 
                    className="rounded-full"
                    style={{ backgroundColor: typeConfig.color, color: 'var(--lk-warm-sand)' }}
                    onClick={() => navigate(`/institutions/${institutionId}/classes/create`)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Class
                  </Button>
                )}
              </div>
              
              {institution.classes?.length > 0 ? (
                <div className="space-y-2">
                  {institution.classes.map((classItem, idx) => (
                    <Card 
                      key={classItem.class_id || idx}
                      className="p-3 flex items-center gap-3 cursor-pointer hover:shadow-sm"
                      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                      onClick={() => navigate(`/institutions/${institutionId}/classes/${classItem.class_id}`)}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)' }}
                      >
                        <BookOpen className="w-5 h-5" style={{ color: 'var(--lk-earth-brown)' }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>{classItem.name}</p>
                        <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                          {classItem.student_count || 0} students · {classItem.teacher_name || 'No teacher assigned'}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
                  <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>No classes yet</p>
                  {isSteward && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 rounded-full"
                      onClick={() => navigate(`/institutions/${institutionId}/classes/create`)}
                    >
                      Create First Class
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
